import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { usePaneQuery, usePaneRedirect, useWorkspace } from "../../../shell/workspace";
import { getCandidates, getUnits, markUnit, type MarkVerdict } from "../../../api/content/curationApi";
import { getReviewDiscussion } from "../../../api/content/discussionsApi";
import { listReviewRequests } from "../../../api/content/reviewCollaborationApi";
import { getReview } from "../../../api/content/reviewsApi";
import ErrorNotice from "../../../errors/ErrorNotice";
import { useLoad } from "../../../shared/loading/useLoad";
import { useSubmission } from "../../../shared/state/useSubmission";
import LoadingIndicator from "../../../ui/feedback/LoadingIndicator";
import CollapsibleColumn from "../../../ui/layout/CollapsibleColumn";
import { useStoredOpen } from "../../../ui/layout/useStoredOpen";
import AppButton from "../../../ui/buttons/AppButton";
import { useMemberNames } from "../../threads/data/useMemberNames";
import { useRunMargins } from "../../threads";
import ContextPane from "../context/ContextPane";
import PresenceBar from "../context/PresenceBar";
import { othersAt } from "../context/presenceNames";
import DownloadMenu from "../decide/DownloadMenu";
import FocusPane from "../focus/FocusPane";
import OutlinePane from "../outline/OutlinePane";
import { adjacentUnit, nextUnresolved, openDecisionCount, outlineRows, reviewOutline, type OutlineFilter } from "../outline/reviewOutline";
import { allows, type DecisionAction } from "../state/decisionRules";
import { KEY_HELP, reviewKeyAction } from "../state/keyboard";
import { INITIAL_REVIEW_PAGE_STATE, reviewPageReducer } from "../state/reviewPageState";
import { useReviewPresence } from "../state/useReviewPresence";
import DecisionBar from "./DecisionBar";
import MergeBlocksNotice from "./MergeBlocksNotice";
import ReviewPanes from "./ReviewPanes";
import ReviewHeader from "./ReviewHeader";
import { chipClassName } from "../../../ui/styles/chipStyles";
import StatusMessage from "../../../ui/feedback/StatusMessage";
import LoadStatus from "../../../ui/feedback/LoadStatus";
import { appPaths } from "../../../shared/routing/appPaths";
import { waitingReviewStep } from "../inbox/reviewAddress";

/**
 * Where the review shows (ADR-0031 D4). `step`: in the current-step area of the Thread whose
 * step decides it. `address`: at `/reviews/<change set>`, which hands a change set with an
 * owning step to that Thread and step, and shows one made outside any Workflow (API,
 * companion apps, imports) on its own until ensured review Threads exist.
 */
export type ReviewPlace = { readonly kind: "step"; readonly threadId: string } | { readonly kind: "address" };

interface ReviewWorkspaceProps {
  readonly changeSetId: string;
  readonly place: ReviewPlace;
}

/**
 * One change set's review in three panes (curation-ui.md CU-1), `?unit=<unit key>` in focus:
 * the outline of review units, the unit in focus, and its context. The outline and the
 * context close to a rail and stay so on every review (web-app.md WA-1). The decision stays
 * in view at the top: approve, request changes, scrap or merge exactly the head shown. Where
 * a Thread step decides the change set, module B's routes answer through that step (P3), so
 * the decision is the step's answer. Every write reloads the review, the units with their
 * marks, and the proposals.
 */
export default function ReviewWorkspace({ changeSetId, place }: ReviewWorkspaceProps) {
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage ?? "en";
  const [search, setSearch] = usePaneQuery();
  const redirect = usePaneRedirect();
  const loadReview = useCallback((signal: AbortSignal) => getReview(changeSetId, signal), [changeSetId]);
  const loadDiscussion = useCallback((signal: AbortSignal) => getReviewDiscussion(changeSetId, signal), [changeSetId]);
  const loadUnits = useCallback((signal: AbortSignal) => getUnits(changeSetId, signal), [changeSetId]);
  const loadCandidates = useCallback((signal: AbortSignal) => getCandidates(changeSetId, signal), [changeSetId]);
  const loadRequests = useCallback((signal: AbortSignal) => listReviewRequests(changeSetId, signal), [changeSetId]);
  const review = useLoad(loadReview);
  const discussion = useLoad(loadDiscussion);
  const units = useLoad(loadUnits);
  const candidates = useLoad(loadCandidates);
  const requests = useLoad(loadRequests);
  const [page, dispatch] = useReducer(reviewPageReducer, INITIAL_REVIEW_PAGE_STATE);
  const [filter, setFilter] = useState<OutlineFilter>("all");
  const [text, setText] = useState("");
  const [editing, setEditing] = useState<string | null>(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const [noteRequests, setNoteRequests] = useState(0);
  const [requested, setRequested] = useState<{ readonly action: DecisionAction; readonly count: number } | null>(null);
  const [storedOutlineOpen, setStoredOutlineOpen] = useStoredOpen("dmw.columns.reviews.outline", true);
  const [storedContextOpen, setStoredContextOpen] = useStoredOpen("dmw.columns.reviews.context", true);
  // In a Run, the outline and the context go to its page's margins where it has them; the review's own page keeps them.
  const runMargins = useRunMargins();
  const margins = place.kind === "step" ? runMargins : null;
  // In a Run without room for margins, both start closed to their rails, so the review's middle has the column's
  // width (usability round 3, 4 Nov: three panes in the column squeezed it to a strip); opening one is for this view only.
  const narrowStep = place.kind === "step" && margins === null;
  const [narrowOutlineOpen, setNarrowOutlineOpen] = useState(false);
  const [narrowContextOpen, setNarrowContextOpen] = useState(false);
  const outlineOpen = narrowStep ? narrowOutlineOpen : storedOutlineOpen;
  const contextOpen = narrowStep ? narrowContextOpen : storedContextOpen;
  const setOutlineOpen = narrowStep ? setNarrowOutlineOpen : setStoredOutlineOpen;
  const setContextOpen = narrowStep ? setNarrowContextOpen : setStoredContextOpen;
  const filterInput = useRef<HTMLInputElement>(null);
  const noteInput = useRef<HTMLInputElement>(null);
  const marking = useSubmission();
  const loaded = review.state.status === "loaded" ? review.state.value : null;
  const people = useMemberNames(loaded?.changeSet.projectId ?? null);
  const head = loaded?.head ?? null;

  useEffect(() => {
    if (head !== null) {
      dispatch({ type: "headLoaded", head });
    }
  }, [head]);

  const reloadAll = useCallback(() => {
    review.refresh();
    discussion.refresh();
    units.refresh();
    candidates.refresh();
    requests.refresh();
  }, [review, discussion, units, candidates, requests]);

  const unitsValue = units.state.status === "loaded" ? units.state.value : null;
  // The bulk offer and its submit belong to one head: while the units and the review name different heads, the
  // offer waits and both are read again, once for each pair of heads.
  const unitsHead = unitsValue?.head ?? null;
  const { refresh: refreshReview } = review;
  const { refresh: refreshUnits } = units;
  useEffect(() => {
    if (head !== null && unitsHead !== null && head !== unitsHead) {
      refreshReview();
      refreshUnits();
    }
  }, [head, unitsHead, refreshReview, refreshUnits]);
  const listing = candidates.state.status === "loaded" ? candidates.state.value : null;
  const changeSetOpen = loaded !== null && (loaded.changeSet.state === "open" || loaded.changeSet.state === "sent_back");
  // Decided by module B on the change set's own project, not on the one chosen above.
  const canEdit = loaded !== null && allows(loaded, "edit");
  const inputs = useMemo(
    () =>
      loaded === null || unitsValue === null
        ? null
        : { review: loaded, units: unitsValue.units, marks: unitsValue.marks, candidates: listing?.candidates ?? [], viewer: people.currentUserId, viewerCanEdit: canEdit, language },
    [loaded, unitsValue, listing, people.currentUserId, canEdit, language],
  );
  const groups = useMemo(() => (inputs === null ? [] : reviewOutline(inputs, filter, text)), [inputs, filter, text]);
  const rows = useMemo(() => (inputs === null ? [] : outlineRows(inputs)), [inputs]);
  const current = search.get("unit") ?? "conversation";
  const row = rows.find((entry) => entry.key === current) ?? rows[0];
  const presence = useReviewPresence(changeSetId, row?.key ?? null, head, reloadAll);
  const presentAt = useCallback((key: string) => othersAt(presence.present, key, people.currentUserId, people.nameOf), [presence.present, people]);
  const covered = new Set(loaded?.summary.callerCurates.map((item) => item.itemId) ?? []);
  const canMark = row !== undefined && changeSetOpen && (row.unit.items.length === 0 || row.unit.items.some((item) => covered.has(item.itemId)));
  const mine = rows.filter((entry) => entry.unit.items.some((item) => covered.has(item.itemId)));
  const unmarked = mine.filter((entry) => entry.myMark !== "ok").length;
  const prefill = (unitsValue?.marks ?? [])
    .filter((mark) => mark.reviewer === people.currentUserId && mark.verdict === "needs_change" && mark.current)
    .map((mark) => `- ${rows.find((entry) => entry.key === mark.unitKey)?.name ?? mark.unitKey}`)
    .join("\n");

  const select = useCallback(
    (key: string | null) => {
      if (key !== null) {
        setEditing(null);
        // In a Thread, the step and the rest of its address stay.
        const next = new URLSearchParams(search);
        next.set("unit", key);
        setSearch(next);
      }
    },
    [search, setSearch],
  );
  const mark = useCallback(
    (verdict: MarkVerdict | "cleared", note: string) => {
      if (loaded === null || row === undefined) {
        return;
      }
      marking.submit(async () => {
        await markUnit(changeSetId, { head: loaded.head, unitKey: row.key, verdict, note });
        units.refresh();
        discussion.refresh();
      });
    },
    [loaded, row, marking, changeSetId, units, discussion],
  );

  // The unit's discussion is in the context column: open it first, then focus once it shows.
  // This review's own column, not one of a review in another tab (ADR-0039).
  const discussionSection = useRef<HTMLElement>(null);
  const openDiscussion = useCallback(
    (scroll: boolean) => {
      setContextOpen(true);
      requestAnimationFrame(() => {
        const target = discussionSection.current;
        if (scroll) {
          target?.scrollIntoView({ behavior: "smooth", block: "start" });
        }
        target?.querySelector<HTMLTextAreaElement>("textarea")?.focus();
      });
    },
    [setContextOpen],
  );

  // Only the tab worked in answers the keys; a review in another tab waits (F90).
  const { isActive } = useWorkspace();
  useEffect(() => {
    if (!isActive) return undefined;
    const onKey = (event: KeyboardEvent) => {
      const action = reviewKeyAction(event);
      if (action === null || row === undefined) {
        return;
      }
      // Escape is the review's only while it closes something; else it stays the page's Back (F87).
      if (action === "close" && !helpOpen && editing === null) {
        return;
      }
      const run: Record<typeof action, () => void> = {
        nextUnit: () => {
          select(adjacentUnit(groups, row.key, 1));
        },
        previousUnit: () => {
          select(adjacentUnit(groups, row.key, -1));
        },
        nextUnresolved: () => {
          select(nextUnresolved(rows, row.key, 1));
        },
        previousUnresolved: () => {
          select(nextUnresolved(rows, row.key, -1));
        },
        markOk: () => {
          if (canMark) {
            mark(row.myMark === "ok" ? "cleared" : "ok", "");
          }
        },
        markNeedsChange: () => {
          if (canMark) {
            setNoteRequests((count) => count + 1);
          }
        },
        comment: () => {
          openDiscussion(true);
        },
        toggleTechnical: () => {
          dispatch({ type: "technicalToggled" });
        },
        filter: () => {
          // A closed outline opens first; its filter can take focus once it shows.
          setOutlineOpen(true);
          requestAnimationFrame(() => {
            filterInput.current?.focus();
          });
        },
        toggleOutline: () => {
          setOutlineOpen(!outlineOpen);
        },
        toggleContext: () => {
          setContextOpen(!contextOpen);
        },
        approve: () => {
          setRequested((last) => ({ action: "approve", count: (last?.count ?? 0) + 1 }));
        },
        requestChanges: () => {
          setRequested((last) => ({ action: "request_changes", count: (last?.count ?? 0) + 1 }));
        },
        merge: () => {
          setRequested((last) => ({ action: "merge", count: (last?.count ?? 0) + 1 }));
        },
        help: () => {
          setHelpOpen((open) => !open);
        },
        close: () => {
          setHelpOpen(false);
          setEditing(null);
        },
      };
      event.preventDefault();
      run[action]();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
    };
  }, [isActive, row, rows, groups, select, canMark, mark, openDiscussion, outlineOpen, setOutlineOpen, contextOpen, setContextOpen, helpOpen, editing]);

  const subject = loaded?.reviewSubject ?? null;
  const waitingStep = loaded === null ? null : waitingReviewStep(loaded);
  const handOver = place.kind === "address" && waitingStep !== null;
  useEffect(() => {
    if (handOver) {
      redirect(appPaths.threadStep(waitingStep.threadId, waitingStep.stepKey, search.get("unit")));
    }
  }, [handOver, waitingStep, redirect, search]);

  if (handOver) return <LoadingIndicator label={t("common.loading")} className="py-10" />;
  if (review.state.status !== "loaded") return <LoadStatus state={review.state} loadingLabel={t("common.loading")} loadingClassName="py-10" onRetry={review.reload} />;
  const value = review.state.value;
  if (place.kind === "step" && subject !== null && subject.threadId !== place.threadId) {
    // A fork shows the change set it inherited; only the owning Thread answers it (P3).
    return (
      <p className="text-sm">
        {t("reviews.inThread.elsewhere")}{" "}
        <Link to={appPaths.threadStep(subject.threadId, subject.stepKey)} className="app-link underline">{t("reviews.inThread.openOwner")}</Link>
      </p>
    );
  }
  const preloaded = discussion.state.status === "loaded" ? discussion.state.value : null;
  const approverNames = (entry: (typeof rows)[number]) =>
    entry.unit.items.flatMap((item) => value.summary.coverage.find((coverage) => coverage.item.itemId === item.itemId)?.approvedBy ?? []).map((userId) => (userId === people.currentUserId ? t("reviews.you") : people.nameOf(userId)));

  return (
    <div className={place.kind === "address" ? "mx-auto max-w-[110rem] space-y-3 py-4" : "space-y-3"}>
      <div className="flex flex-wrap items-start gap-2">
        <div className="min-w-0 flex-1">
          <ReviewHeader review={value} compact={place.kind === "step"} />
          {place.kind === "address" && subject !== null ? (
            <p className="text-sm">
              <Link to={appPaths.thread(subject.threadId)} className="app-link underline">{t("reviews.inThread.decidedIn")}</Link>
            </p>
          ) : null}
        </div>
        <PresenceBar
          present={presence.present}
          people={people}
          unitName={(key) => (key === null ? "" : key === "conversation" ? t("reviews.curation.outline.conversation") : (rows.find((entry) => entry.key === key)?.name ?? key))}
        />
        <DownloadMenu review={value} units={unitsValue?.units ?? []} candidates={listing} />
        <button
          type="button"
          className={chipClassName()}
          aria-expanded={helpOpen}
          aria-keyshortcuts="?"
          onClick={() => {
            setHelpOpen(!helpOpen);
          }}
        >
          {t("reviews.curation.keys.title")}
        </button>
      </div>
      {helpOpen ? (
        <section className="app-card p-3" aria-label={t("reviews.curation.keys.title")}>
          <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
            {KEY_HELP.map(([keys, label]) => (
              <div key={label} className="contents">
                <dt className="font-mono">{keys}</dt>
                <dd>{t(`reviews.curation.keys.${label}`)}</dd>
              </div>
            ))}
          </dl>
        </section>
      ) : null}
      <MergeBlocksNotice review={value} technical={page.technical} onChanged={reloadAll} />
      <DecisionBar
        review={value}
        state={page}
        dispatch={dispatch}
        openCount={openDecisionCount(rows)}
        unmarked={unmarked}
        prefill={prefill}
        requested={requested}
        onChanged={reloadAll}
        onNextOpen={() => {
          select(nextUnresolved(rows, row?.key ?? null, 1));
        }}
      />
      {presence.newHead ? (
        <div role="status">
          <StatusMessage tone="info" className="px-3 py-2 text-sm">
            <p className="flex flex-wrap items-center gap-2">
              {t("reviews.collaboration.presence.newHead")}
              <AppButton size="xs" onClick={reloadAll}>
                {t("reviews.collaboration.presence.reload")}
              </AppButton>
            </p>
          </StatusMessage>
        </div>
      ) : null}
      {marking.state.status === "failed" ? <ErrorNotice error={marking.state.error} compact={true} /> : null}
      {units.state.status === "failed" ? <ErrorNotice error={units.state.error} onRetry={units.reload} compact={true} /> : null}
      {candidates.state.status === "failed" ? <ErrorNotice error={candidates.state.error} onRetry={candidates.reload} compact={true} /> : null}
      {row === undefined ? (
        <LoadingIndicator label={t("common.loading")} className="py-6" />
      ) : (
        <ReviewPanes
          margins={margins}
          outlineOpen={outlineOpen}
          contextOpen={contextOpen}
          outline={
          <CollapsibleColumn
            label={t("reviews.curation.columns.outline")}
            side="left"
            open={outlineOpen}
            onOpenChange={setOutlineOpen}
            className={margins === null ? "self-start pane-md:sticky pane-md:top-2" : undefined}
            contentClassName="app-card pane-md:max-h-[calc(100vh-1rem)] pane-md:overflow-y-auto p-2"
            railClassName="pane-md:min-h-[12rem] pane-md:flex-col pane-md:px-0 pane-md:py-3"
            railLabelClassName="pane-md:[writing-mode:vertical-rl]"
          >
            <OutlinePane
              groups={groups}
              current={row.key}
              filter={filter}
              text={text}
              filterInput={filterInput}
              onFilter={setFilter}
              onText={setText}
              onSelect={select}
              approverNames={approverNames}
              presentAt={presentAt}
            />
          </CollapsibleColumn>
          }
          focus={
          <div className="app-card min-w-0 p-3" role="region" aria-label={t("reviews.curation.focusLabel")}>
            <FocusPane
              key={`${row.key}:${value.head}`}
              row={row}
              rows={rows}
              review={value}
              candidates={listing}
              discussion={preloaded}
              othersHere={presentAt(row.key)}
              canEdit={canEdit}
              canMark={canMark}
              canMarkInBulk={changeSetOpen && mine.length > 0 && unitsHead === head}
              technical={page.technical}
              editing={editing}
              noteInput={noteInput}
              noteRequests={noteRequests}
              onMark={mark}
              onComment={() => {
                openDiscussion(false);
              }}
              onFocusItem={(itemId) => {
                const target = rows.find((entry) => entry.key === `item:${itemId}` || entry.key === `affected:${itemId}` || entry.unit.items.some((item) => item.itemId === itemId));
                select(target?.key ?? null);
              }}
              onEditDone={() => {
                setEditing(null);
              }}
              onChanged={reloadAll}
            />
          </div>
          }
          context={
          <CollapsibleColumn
            label={t("reviews.curation.columns.context")}
            side="right"
            open={contextOpen}
            onOpenChange={setContextOpen}
            className={margins === null ? "pane-md:col-span-2 pane-lg:col-span-1" : undefined}
            contentClassName={margins === null ? "app-card p-3" : "app-card max-h-[calc(100vh-1rem)] overflow-y-auto p-3"}
            railClassName="pane-lg:sticky pane-lg:top-2 pane-lg:min-h-[12rem] pane-lg:flex-col pane-lg:px-0 pane-lg:py-3"
            railLabelClassName="pane-lg:[writing-mode:vertical-rl]"
          >
            <ContextPane
              unit={row.unit}
              review={value}
              marks={unitsValue?.marks ?? []}
              discussion={preloaded}
              canEdit={canEdit}
              canMark={canMark}
              requests={requests.state.status === "loaded" ? requests.state.value : []}
              canAsk={allows(value, "request_review")}
              onEdit={setEditing}
              onDiscussionChanged={discussion.refresh}
              onRequestsChanged={requests.refresh}
              discussionRef={discussionSection}
            />
          </CollapsibleColumn>
          }
        />
      )}
    </div>
  );
}
